/**
 * Progress Queries
 *
 * Shared query utilities for user progress database operations.
 * Implements DRY principles for database access.
 *
 * @see src/services/progress/progress.service.ts for service layer
 */

import { supabase } from '@/services/supabase';
import type { Database } from '@/types/database.types';

/**
 * Shared progress query utilities
 * Base query pattern for DRY database access
 */
export const progressQueries = {
  /**
   * Base query for user_progress table
   */
  baseSelect: () =>
    supabase.from('user_progress').select('*'),

  /**
   * Get user progress by user ID and language.
   *
   * Uses .maybeSingle() (not .single()) because the row is created lazily on
   * first conversation completion via incrementConversations — a brand-new
   * user has no row, and .single() would return HTTP 406 on every page load
   * until they finish a conversation. .maybeSingle() returns { data: null,
   * error: null } in that case; all callers already check `if (!current)`.
   */
  getByUserAndLanguage: (userId: string, languageCode: string = 'es') =>
    progressQueries.baseSelect()
      .eq('user_id', userId)
      .eq('language', languageCode)
      .maybeSingle(),

  /**
   * Update user progress (upsert)
   */
  upsertProgress: (
    userId: string,
    languageCode: string,
    updates: Partial<Database['public']['Tables']['user_progress']['Update']>
  ) =>
    supabase
      .from('user_progress')
      .upsert({
        user_id: userId,
        language: languageCode,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single(),

  /**
   * Increment conversation count
   */
  incrementConversations: async (userId: string, languageCode: string = 'es') => {
    const { data: current } = await progressQueries.getByUserAndLanguage(userId, languageCode);

    if (!current) {
      return progressQueries.upsertProgress(userId, languageCode, {
        total_conversations: 1,
      });
    }

    return progressQueries.upsertProgress(userId, languageCode, {
      total_conversations: (current.total_conversations || 0) + 1,
    });
  },

  /**
   * Update daily streak
   * Note: This is a simplified version that updates streak based on current_streak field
   */
  updateStreak: async (userId: string, languageCode: string = 'es') => {
    const { data: current } = await progressQueries.getByUserAndLanguage(userId, languageCode);

    if (!current) {
      return progressQueries.upsertProgress(userId, languageCode, {
        current_streak: 1,
        longest_streak: 1,
      });
    }

    // Increment streak (actual date logic should be in service layer)
    const newStreak = (current.current_streak || 0) + 1;

    return progressQueries.upsertProgress(userId, languageCode, {
      current_streak: newStreak,
      longest_streak: Math.max(newStreak, current.longest_streak || 0),
    });
  },
};
